import { IStudentRegistration } from 'src/interfaces/family'

export interface CurriculumTag {
  key: string
  value: string
}

export const curriculumTagOptions = [
  {
    value: 'focusAreaReport',
    labelKey: 'focusAreaReport',
    descriptionKey: 'curriculumTagFocusAreaReportDescription',
    icon: 'fact_check',
  },
  {
    value: 'progressReport',
    labelKey: 'progressReport',
    descriptionKey: 'curriculumTagProgressReportDescription',
    icon: 'timeline',
  },
  {
    value: 'journals',
    labelKey: 'journals',
    descriptionKey: 'curriculumTagJournalsDescription',
    icon: 'menu_book',
  },
] as const

export type CurriculumTagValue = (typeof curriculumTagOptions)[number]['value']

const validAreaValues: string[] = curriculumTagOptions.map(({ value }) => value)

export const defaultCurriculumTags: CurriculumTag[] = curriculumTagOptions.map(
  ({ value }) => ({ key: 'area', value }),
)

export const normalizeCurriculumTags = (tags?: CurriculumTag[] | null) => {
  if (!tags) return []
  return tags.filter(
    (tag) =>
      (tag.key === 'area' && validAreaValues.includes(tag.value)) ||
      tag.key === 'group',
  )
}

export const getAreaTags = (tags?: CurriculumTag[] | null) =>
  normalizeCurriculumTags(tags)
    .filter((tag) => tag.key === 'area')
    .map((tag) => tag.value as CurriculumTagValue)

export const getGroupTags = (tags?: CurriculumTag[] | null): string[] =>
  normalizeCurriculumTags(tags)
    .filter((tag) => tag.key === 'group')
    .map((tag) => tag.value)

export const isTagSelected = (
  tags: CurriculumTag[] | undefined,
  tagValue: CurriculumTagValue,
) => getAreaTags(tags).includes(tagValue)

export const toggleTag = (
  tags: CurriculumTag[] | undefined,
  tagValue: CurriculumTagValue,
) => {
  const currentTags = normalizeCurriculumTags(tags)
  const hasTag = currentTags.some(
    (tag) => tag.key === 'area' && tag.value === tagValue,
  )

  if (hasTag) {
    return currentTags.filter(
      (tag) => !(tag.key === 'area' && tag.value === tagValue),
    )
  }

  return [...currentTags, { key: 'area', value: tagValue }]
}

export const setGroupTags = (
  tags: CurriculumTag[] | undefined,
  groupIds: string[],
) => {
  const nonGroupTags = normalizeCurriculumTags(tags).filter(
    (tag) => tag.key !== 'group',
  )
  const groupTags = groupIds.map((id) => ({ key: 'group', value: id }))
  return [...nonGroupTags, ...groupTags]
}

export const getCurriculumTagDisplay = (tags?: CurriculumTag[]) => {
  const areaTags = getAreaTags(tags)
  return curriculumTagOptions.filter((tagOption) =>
    areaTags.includes(tagOption.value),
  )
}

export const hasAreaTag = (
  tags: CurriculumTag[] | undefined | null,
  tagValue: string,
) => getAreaTags(tags).includes(tagValue as CurriculumTagValue)

export const matchesGroup = (
  tags: CurriculumTag[] | undefined | null,
  groupId: string | null | undefined,
) => {
  const groupTags = getGroupTags(tags)
  if (groupTags.length === 0) return true
  if (!groupId) return true
  return groupTags.includes(groupId)
}

export const studentMatchesGroup = (
  tags: CurriculumTag[] | undefined | null,
  student: { registrations: IStudentRegistration[] },
  status: string | null = null,
) => {
  const groupTags = getGroupTags(tags)
  if (groupTags.length === 0) return true
  const studentGroupIds = student.registrations
    .filter((reg) => !status || reg.status === status)
    .map((reg) => reg.group.id)
  return groupTags.some((groupTag) => studentGroupIds.includes(groupTag))
}
